"use client";

import React, { useRef, useEffect } from "react";

interface AnimatedThunderProps {
  /** Color for the cloud's outer shape */
  cloudColor?: string;
  /** Color for the cloud's internal shape */
  cloudInnerColor?: string;
  /** Color for the lightning bolt */
  thunderColor?: string;
  /** Canvas size in pixels (width & height). */
  size?: number;
  /** Additional CSS classes. */
  className?: string;
  /** Speed multiplier for the animation. */
  speed?: number;
}

const AnimatedThunder: React.FC<AnimatedThunderProps> = ({
  cloudColor = "#444444",
  cloudInnerColor = "#333333",
  thunderColor = "#00BFFF", // Blue lightning color
  size = 64,
  className = "",
  speed = 1.0,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const requestRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const TAU = 2 * Math.PI;
  const STROKE = 0.08;

  /**
   * Draws a filled circle.
   */
  function circle(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    r: number
  ) {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU, false);
    ctx.fill();
  }

  /**
   * Draws a set of "puffs" that together look like a cloud shape.
   */
  function puffs(
    ctx: CanvasRenderingContext2D,
    t: number,
    cx: number,
    cy: number,
    rx: number,
    ry: number,
    rmin: number,
    rmax: number
  ) {
    function puff(
      ctx: CanvasRenderingContext2D,
      t: number,
      cx: number,
      cy: number,
      rx: number,
      ry: number,
      rmin: number,
      rmax: number
    ) {
      const c = Math.cos(t * TAU);
      const s = Math.sin(t * TAU);
      const rm = rmax - rmin;

      circle(
        ctx,
        cx - s * rx,
        cy + c * ry + rm * 0.5,
        rmin + (1 - c * 0.5) * rm
      );
    }

    for (let i = 0; i < 5; i++) {
      puff(ctx, t + i / 5, cx, cy, rx, ry, rmin, rmax);
    }
  }

  /**
   * Draws a two-tone cloud: an outer color + an inner color.
   */
  function cloud(
    ctx: CanvasRenderingContext2D,
    t: number,
    cx: number,
    cy: number,
    cw: number,
    s: number,
    outerColor: string,
    innerColor: string
  ) {
    // slow drift
    t /= 30000;

    const a = cw * 0.21;
    const b = cw * 0.12;
    const c = cw * 0.24;
    const d = cw * 0.28;

    // --- 1) Draw the outer cloud shape ---
    ctx.fillStyle = outerColor;
    puffs(ctx, t, cx, cy, a, b, c, d);

    // --- 2) Carve out a smaller region in the center to make room for another color ---
    ctx.save();
    ctx.globalCompositeOperation = "destination-out";
    puffs(ctx, t, cx, cy, a, b, c - s, d - s);
    ctx.restore();

    // --- 3) Fill that smaller region with the 'innerColor' ---
    ctx.fillStyle = innerColor;
    puffs(ctx, t, cx, cy, a, b, c - s, d - s);
  }

  /**
   * Draws lightning bolts with a simple, bold design.
   */
  function thunder(
    ctx: CanvasRenderingContext2D,
    t: number,
    cx: number,
    cy: number,
    cw: number,
    color: string,
    speedVal: number
  ) {
    // Control timing of lightning flashes
    const flashCycle = 2000 / speedVal; // Longer pauses between flashes
    t = t % flashCycle;

    // Make lightning appear only for brief moments
    const flashDuration = flashCycle * 0.15; // 15% of the cycle
    if (t < flashDuration) {
      // Calculate flash brightness
      let alpha = 0;
      if (t < flashDuration / 2) {
        // Fade in quickly
        alpha = (t / (flashDuration / 2)) * 0.9;
      } else {
        // Fade out more slowly
        alpha = 0.9 * (1 - ((t - flashDuration / 2) / (flashDuration / 2)));
      }

      // Size of the bolt
      const boltSize = cw / 15;

      // Center position
      const x = cx;
      const y = cy + cw * 0.13; // Just below the cloud

      // Draw the glow first
      ctx.fillStyle = color;
      ctx.globalAlpha = alpha * 0.5;
      drawBolt(ctx, x, y, boltSize * 1.3);

      // Then draw the main bolt
      ctx.globalAlpha = alpha;
      drawBolt(ctx, x, y, boltSize);

      // Reset alpha
      ctx.globalAlpha = 1.0;
    }
  }

  /**
   * Helper function to draw a single lightning bolt
   */
  function drawBolt(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number
  ) {
    ctx.beginPath();

    // Simple, bold lightning bolt shape
    ctx.moveTo(x, y); // Top center
    ctx.lineTo(x - size, y + size * 2); // Left zag
    ctx.lineTo(x + size * 0.3, y + size * 3); // Right zag
    ctx.lineTo(x - size * 0.5, y + size * 5); // Left zag
    ctx.lineTo(x + size * 1.2, y + size * 4); // Right branch
    ctx.lineTo(x, y + size * 3); // Back to middle
    ctx.lineTo(x + size * 0.7, y + size); // Right zag on way up
    ctx.closePath();

    ctx.fill();
  }

  /**
   * Clears the canvas and draws one frame of the animation.
   */
  function drawFrame(ctx: CanvasRenderingContext2D, elapsed: number) {
    const w = ctx.canvas.width;
    const h = ctx.canvas.height;
    const s = Math.min(w, h);

    // Clear canvas
    ctx.clearRect(0, 0, w, h);

    // Draw lightning bolts
    thunder(ctx, elapsed, w * 0.5, h * 0.4, s * 0.9, thunderColor, speed);

    // Draw the two-tone cloud on top
    cloud(ctx, elapsed, w * 0.5, h * 0.37, s * 0.9, s * STROKE, cloudColor, cloudInnerColor);
  }

  /**
   * Main animation loop using requestAnimationFrame.
   */
  const animate = (timestamp: number) => {
    if (startTimeRef.current === null) {
      startTimeRef.current = timestamp;
    }
    const elapsed = timestamp - startTimeRef.current;

    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        drawFrame(ctx, elapsed);
      }
    }
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => {
      if (requestRef.current !== null) {
        cancelAnimationFrame(requestRef.current);
      }
    };
    // Re-run if any props change
  }, [cloudColor, cloudInnerColor, thunderColor, size, speed]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      width={size}
      height={size}
      style={{ width: size, height: size }}
    />
  );
};

export default AnimatedThunder;